import { z } from 'zod';

const LifecycleIntSchema = z.number().int().min(0).nullable().optional();

export const CatalogGenericServiceIdParamsSchema = z.object({
  id: z.uuid(),
});

export const CreateCatalogGenericServiceBodySchema = z.strictObject({
  canonicalName: z.string().trim().min(1).max(255),
  categoryId: z.uuid(),
  lc116Code: z.string().trim().min(1).max(16).nullable().optional(),
  taxCodeNational: z.string().trim().min(1).max(16).nullable().optional(),
  unitOfMeasureCode: z.string().trim().min(1).max(16).nullable().optional(),
  usefulLifeMonths: LifecycleIntSchema,
  usefulLifeMeters: LifecycleIntSchema,
  warrantyMonths: LifecycleIntSchema,
  warrantyMeters: LifecycleIntSchema,
  estimatedDurationMinutes: LifecycleIntSchema,
});

export const UpdateCatalogGenericServiceBodySchema =
  CreateCatalogGenericServiceBodySchema.partial();

export const ArchiveCatalogGenericServiceBodySchema = z.preprocess(
  (value) => value ?? {},
  z.object({
    reason: z.string().trim().max(500).optional(),
  }),
);

export const ListCatalogGenericServicesQuerySchema = z.looseObject({
  q: z.string().trim().min(1).max(200).optional(),
  categoryId: z.uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const CatalogGenericServiceResponseSchema = z.object({
  id: z.uuid(),
  canonicalName: z.string(),
  categoryId: z.uuid(),
  lc116Code: z.string().nullable(),
  taxCodeNational: z.string().nullable(),
  unitOfMeasureCode: z.string().nullable(),
  usefulLifeMonths: z.number().int().nullable(),
  usefulLifeMeters: z.number().int().nullable(),
  warrantyMonths: z.number().int().nullable(),
  warrantyMeters: z.number().int().nullable(),
  estimatedDurationMinutes: z.number().int().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable(),
  deletedByUserId: z.uuid().nullable(),
  deleteReason: z.string().nullable(),
});

export const CatalogGenericServiceListItemSchema = CatalogGenericServiceResponseSchema.extend({
  categoryName: z.string(),
});

export const CatalogGenericServiceListResponseSchema = z.object({
  items: z.array(CatalogGenericServiceListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
    total: z.number().int().optional(),
  }),
});

export type CatalogGenericServiceIdParams = z.infer<typeof CatalogGenericServiceIdParamsSchema>;
export type CreateCatalogGenericServiceBody = z.infer<typeof CreateCatalogGenericServiceBodySchema>;
export type UpdateCatalogGenericServiceBody = z.infer<typeof UpdateCatalogGenericServiceBodySchema>;
export type ArchiveCatalogGenericServiceBody = z.infer<
  typeof ArchiveCatalogGenericServiceBodySchema
>;
export type ListCatalogGenericServicesQuery = z.infer<typeof ListCatalogGenericServicesQuerySchema>;
export type CatalogGenericServiceResponse = z.infer<typeof CatalogGenericServiceResponseSchema>;
export type CatalogGenericServiceListItem = z.infer<typeof CatalogGenericServiceListItemSchema>;
export type CatalogGenericServiceListResponse = z.infer<
  typeof CatalogGenericServiceListResponseSchema
>;
